
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import Navbar from "../../../../globals/components/Navbar";
import { useParams } from "react-router-dom";
import { apiClient } from "../../../../api/httpClient";
import { toast } from "react-toastify";

function ParalegalDetail() {
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true); // eslint-disable-line no-unused-vars

    // Edit Mode State
    const [isEditing, setIsEditing] = useState(false); // eslint-disable-line no-unused-vars
    const [editForm, setEditForm] = useState({
        prefix: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        suffix: "",
        email: "",
        phone_number: "",
        extension: "",
        position: "",
        is_admin: false,
        firmEmail: "",
        useFirmEmail: false,
    });
    // Modal State
    const [openAddModal, setOpenAddModal] = useState(false);
    const [allLawFirms, setAllLawFirms] = useState([]);
    const [firmLocations, setFirmLocations] = useState([]);
    const [selectedFirm, setSelectedFirm] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("");
    const [loadingLocations, setLoadingLocations] = useState(false);

    useEffect(() => {
        fetchData();
        fetchLawFirms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchLawFirms = async () => {
        try {
            const response = await apiClient("/list-lawfirms/", "GET");
            setAllLawFirms(response);
        } catch (error) {
            console.error("Error fetching firms:", error);
        }
    };

    const formatDateTime = (value) => {
        if (!value) return "—";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleString();
      };

    const handleFirmChange = async (firmId) => {
        setSelectedFirm(firmId);
        setSelectedLocation("");
        setLoadingLocations(true);
        try {
            const response = await apiClient(`/get-locations-by-lawfirm/${firmId}`, "GET");
            setFirmLocations(response);
        } catch (error) {
            console.error("Error fetching locations:", error);
        } finally {
            setLoadingLocations(false);
        }
    };

    const handleAddLocation = async () => {
        if (!selectedFirm || !selectedLocation) return;
        try {
            await apiClient(`/mappings/`, "POST", {
                lawfirm_user: id,
                law_firm_location: selectedLocation,
                lawfirm: selectedFirm
            });
            toast.success("Location added successfully");
            setOpenAddModal(false);
            fetchData();
            setSelectedFirm("");
            setSelectedLocation("");
        } catch (error) {
            console.error("Error adding location:", error);
            toast.error(error?.detail || error?.error || "Failed to add location");
        }
    };

    const handleRemoveLocation = async (firmId, locationId) => {
        if (!window.confirm("Are you sure you want to remove this location access?")) return;
        try {
            await apiClient(`/mappings/`, "DELETE", {
                lawfirm_user: id,
                law_firm_location: locationId
            });
            toast.success("Location removed successfully");
            fetchData();
        } catch (error) {
            console.error("Error removing location:", error);
            toast.error("Failed to remove location");
    }
  };

  // ... (fetchData update to populate new fields)

    const fetchData = async () => {
        setLoading(true);
        try {
            const userResponse = await apiClient(`/legal-service-paralegals/${id}/`, "GET");
            setUser(userResponse);

            if (userResponse.mappings) {
                setLocations(userResponse.mappings);
            }

            setEditForm({
                prefix: userResponse.prefix || "",
                first_name: userResponse.user?.first_name || "",
                middle_name: userResponse.middle_name || "",
                last_name: userResponse.user?.last_name || "",
                suffix: userResponse.suffix || "",
                email: userResponse.user?.email || "",
                phone_number: userResponse.phone_number || "",
                extension: userResponse.extension || "",
                position: userResponse.position || "Paralegal",
                is_admin: userResponse.is_admin || false,
                firmEmail: userResponse.firm_email || "",
                useFirmEmail: Boolean(userResponse.firm_email),
            });

        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load paralegal details.");
        } finally {
            setLoading(false);
        }
    };

    // ...

    const handleUpdateUser = async () => {
        try {
            // Mapping fields to match backend expectation for /update-lawfirm-user/
            // Assuming the backend endpoint accepts these fields in the root or nested 'user' dict
            await apiClient(`/update-lawfirm-user/${id}`, "PUT", {
                user: {
                    first_name: editForm.first_name,
                    last_name: editForm.last_name,
                    email: editForm.email
                },
                prefix: editForm.prefix,
                middle_name: editForm.middle_name,
                suffix: editForm.suffix,
                phone_number: editForm.phone_number,
                extension: editForm.extension,
                is_admin: editForm.is_admin,
                firm_email: editForm.useFirmEmail ? editForm.firmEmail : null,

                // Fields we don't change but must preserve
                position: editForm.position,
                is_legal_service: true,
                is_active: true
            });
            toast.success("User details updated successfully");
            setIsEditing(false);
            fetchData();
        } catch (error) {
            console.error("Error updating user:", error);
            toast.error(error.detail || "Failed to update user details");
        }
    }

    // ...

    return (
        <>
            <Navbar />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}>
                    {/* User Header Section - Matches EditLawFirmUser */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                            {user?.prefix} {user?.user?.first_name} {user?.middle_name} {user?.user?.last_name} {user?.suffix}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {user?.position} • {user?.user?.email}
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{
                                ...(user?.is_active ? {} : { color: "warning.main" }),
                            }}
                        >
                            Registration Complete: {user?.is_active ? "Yes" : "No"}
                        </Typography>
                        {user?.is_active && (
                        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                            Username: {user?.user?.username || "—"} • Last Logged In:{" "}
                            {formatDateTime(user?.user?.last_2fa_verified)}
                        </Typography>
                        )}
                    </Box>

                    {/* Form Section */}
                    <Box>
                        <Typography variant="h6" sx={{ marginBottom: 2 }}>1. User Details</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Prefix"
                                    value={editForm.prefix}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, prefix: e.target.value }))}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="First Name"
                                    value={editForm.first_name}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Middle Name"
                                    value={editForm.middle_name}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, middle_name: e.target.value }))}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Last Name"
                                    value={editForm.last_name}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Suffix</InputLabel>
                                    <Select
                                        value={editForm.suffix}
                                        label="Suffix"
                                        onChange={(e) => setEditForm(prev => ({ ...prev, suffix: e.target.value }))}
                                    >
                                        <MenuItem value="">Select suffix</MenuItem>
                                        <MenuItem value="Jr.">Jr.</MenuItem>
                                        <MenuItem value="Sr.">Sr.</MenuItem>
                                        <MenuItem value="II">II</MenuItem>
                                        <MenuItem value="III">III</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Position"
                                    value={editForm.position}
                                    fullWidth
                                    disabled
                                    select
                                >
                                    <MenuItem value={editForm.position}>{editForm.position}</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Permission Level"
                                    value={editForm.is_admin ? "Yes" : "No"}
                                    fullWidth
                                    select
                                    onChange={(e) => setEditForm(prev => ({ ...prev, is_admin: e.target.value === "Yes" }))}
                                >
                                    <MenuItem value="Yes">Admin</MenuItem>
                                    <MenuItem value="No">User</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    label="Is this individual an Independent Paralegal who works for multiple law firms?"
                                    fullWidth
                                    select
                                    value="Yes"
                                    disabled
                                >
                                    <MenuItem value="Yes">Yes</MenuItem>
                                    <MenuItem value="No">No</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Email"
                                    value={editForm.email}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={editForm.useFirmEmail}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, useFirmEmail: e.target.checked }))}
                                        />
                                    }
                                    label="Use a different email for this firm's communications"
                                />
                            </Grid>
                            {editForm.useFirmEmail && (
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        label="Firm-Specific Email"
                                        value={editForm.firmEmail}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, firmEmail: e.target.value }))}
                                        fullWidth
                                        helperText="This email will be used for communications related to this firm only. Login will still use the primary email above."
                                    />
                                </Grid>
                            )}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Phone Number"
                                    value={editForm.phone_number}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, phone_number: e.target.value }))}
                                    fullWidth
                                    placeholder="(999) 999-9999"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Extension"
                                    value={editForm.extension}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, extension: e.target.value }))}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 4, textAlign: "right" }}>
                            <Button
                                onClick={handleUpdateUser}
                                variant="contained"
                                color="primary"
                            >
                                SAVE
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled
                                style={{ marginLeft: "1rem" }}
                            >
                                RESEND INVITE
                            </Button>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 4, mt: 4 }} />

                    {/* Law Firm Associations */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight="bold">2. Law Firm Locations</Typography>
                        {!openAddModal && (
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenAddModal(true)}
                            >
                                Link Another Lawfirm
                            </Button>
                        )}
                    </Box>

                    {/* Inline Link Law Firm Section */}
                    {openAddModal && (
                        <Box sx={{ mb: 3, pt: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} mb={2}>Link Law Firm</Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 5 }}>
                                    <Autocomplete
                                        options={allLawFirms}
                                        getOptionLabel={(option) => option.name || ""}
                                        value={allLawFirms.find(f => f.lawfirm_id === selectedFirm) || null}
                                        onChange={(event, newValue) => handleFirmChange(newValue ? newValue.lawfirm_id : "")}
                                        renderInput={(params) => <TextField {...params} label="Law Firm" size="small" />}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 5 }}>
                                    <FormControl fullWidth size="small" disabled={!selectedFirm || loadingLocations}>
                                        <InputLabel>Location</InputLabel>
                                        <Select
                                            value={selectedLocation}
                                            label="Location"
                                            onChange={(e) => setSelectedLocation(e.target.value)}
                                        >
                                            {firmLocations.map(loc => (
                                                <MenuItem key={loc.law_firm_location_id} value={loc.law_firm_location_id}>
                                                    {loc.name} - {loc.city}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 2 }} display="flex" alignItems="center">
                                    <Button
                                        variant="contained"
                                        onClick={handleAddLocation}
                                        disabled={!selectedFirm || !selectedLocation}
                                        startIcon={<SaveIcon />}
                                        sx={{ mr: 1 }}
                                    >
                                        Link
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => setOpenAddModal(false)}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
                        <Table>
                            <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                                <TableRow>
                                    <TableCell>Law Firm</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {locations.map((loc) => (
                                    <TableRow key={loc.mapping_id}>
                                        <TableCell sx={{ fontWeight: 600 }}>{loc.law_firm_name}</TableCell>
                                        <TableCell>{loc.location_name}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                color="error"
                                                onClick={() => handleRemoveLocation(loc.lawfirm_id, loc.law_firm_location_id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {locations.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">No law firm associations found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                </Paper>

            </Container>
        </>
    );
}

export default ParalegalDetail;
